// The invoice drawer's history: every email attempt with each recipient's result, and
// every action taken on the invoice. Words come from the server (`failure` on an attempt
// and an action, built by `shared::failure_copy::describe_invoice`) and from the pure
// helpers in `lib/invoicing-view`; nothing here reads a machine string except under
// "Technical details" and "Support details".
//
// Before 2026-10-14 both lists were one 11px monospaced line per row: wire words
// (`initial`, `done`), a provider message id, a red "unknown" pill for every recipient the
// delivery check could not see, and the 2026-09-10 Xero refusals in red beside an invoice
// sent long since.

import { useEffect, useRef, type ReactNode } from 'react';
import type { InvoiceAction, InvoiceEmailAttempt, InvoiceSummary } from '@/api/types';
import { CopyableId } from '@/components/copyable-id';
import { FailureNotice } from '@/components/failure-notice';
import { SyncPill } from '@/components/sync-pill';
import { fmtStampEt } from '@/lib/datetime';
import {
  actionKindLabel,
  actionStatusBand,
  actionStatusLabel,
  actorLabel,
  emailAttemptTitle,
  failureTone,
  recipientResult,
} from '@/lib/invoicing-view';

/** The detail route returns at most this many actions, newest first. */
export const INVOICE_ACTIVITY_LIMIT = 20;

export function EmailAttempts({ emails }: { emails: readonly InvoiceEmailAttempt[] }) {
  if (emails.length === 0) {
    return <p className="inv-empty muted">Not emailed yet.</p>;
  }
  return (
    <ol className="inv-attempts" aria-label="Email attempts">
      {emails.map((attempt) => (
        <EmailAttemptCard key={attempt.id} attempt={attempt} />
      ))}
    </ol>
  );
}

function EmailAttemptCard({ attempt }: { attempt: InvoiceEmailAttempt }) {
  const recipients = [
    ...attempt.to.map((address) => ({ role: 'To', address })),
    ...attempt.cc.map((address) => ({ role: 'Cc', address })),
  ];
  return (
    <li className="inv-attempt" data-testid="invoice-email-attempt">
      <div className="inv-attempt-hd">
        <span className="inv-attempt-title">{emailAttemptTitle(attempt)}</span>
        <span className="muted">
          {actorLabel(attempt.requested_by_name)} · {fmtStampEt(attempt.sent_at ?? attempt.created_at)}
        </span>
      </div>
      {attempt.failure && <FailureNotice failure={attempt.failure} dense announce={false} />}
      <ul className="inv-recipients" aria-label="Recipients">
        {recipients.map(({ role, address }) => {
          const result = recipientResult(attempt, address);
          return (
            <li key={`${role}:${address}`} className="inv-recipient">
              <span className="inv-recipient-role muted">{role}</span>
              <span className="inv-recipient-addr">{address}</span>
              <SyncPill band={result.band} label={result.label} />
            </li>
          );
        })}
      </ul>
      {attempt.status === 'sent' && (
        <p className="inv-attempt-note muted">
          {attempt.delivery_status === 'delivered'
            ? 'Every recipient’s mail server accepted the email.'
            : 'The email service accepted the email; delivery is confirmed separately, and nothing is resent on its own.'}{' '}
          {attempt.delivery_checked_at
            ? `Delivery last checked ${fmtStampEt(attempt.delivery_checked_at)}.`
            : 'Delivery not checked yet.'}
        </p>
      )}
      {attempt.delivery_failure && (
        <div className="inv-delivery-check">
          <FailureNotice failure={attempt.delivery_failure} dense announce={false} tone="warn" />
        </div>
      )}
      {attempt.provider_message_id && (
        <details className="inv-support">
          <summary className="muted">Support details</summary>
          <div className="inv-support-body">
            <span className="muted">Email service message id</span>
            <CopyableId value={attempt.provider_message_id} />
          </div>
        </details>
      )}
    </li>
  );
}

export function InvoiceActivity({
  actions,
  invoice,
}: {
  actions: readonly InvoiceAction[];
  invoice: Pick<InvoiceSummary, 'emailed_at' | 'failure'>;
}) {
  if (actions.length === 0) {
    return <p className="inv-empty muted">Nothing has been done with this invoice yet.</p>;
  }
  return (
    <ol className="inv-activity" aria-label="Activity">
      {actions.map((action) => (
        <ActivityRow key={action.id} action={action} invoice={invoice} />
      ))}
    </ol>
  );
}

function ActivityRow({
  action,
  invoice,
}: {
  action: InvoiceAction;
  invoice: Pick<InvoiceSummary, 'emailed_at' | 'failure'>;
}) {
  const failed = action.status === 'failed';
  const history = action.resolved || action.superseded;
  // The approval failure the invoice is in now already leads the drawer; repeating it in
  // full here is noise. Any other open failure (a sync, a resend) is shown where it is.
  const shownAbove = action.kind === 'approve_send' && invoice.failure != null;
  const collapsed = history || shownAbove;
  return (
    <li
      className={`inv-activity-row${failed && history ? ' is-resolved' : ''}`}
      data-testid="invoice-activity-row"
    >
      <div className="inv-activity-hd">
        <span className="inv-activity-what">{actionKindLabel(action, invoice)}</span>
        <SyncPill band={actionStatusBand(action)} label={actionStatusLabel(action)} />
      </div>
      <div className="inv-activity-meta muted">
        {actorLabel(action.requested_by_name)} · {fmtStampEt(action.created_at)}
        {action.finished_at ? ` · finished ${fmtStampEt(action.finished_at)}` : ''}
      </div>
      {failed && !collapsed && (
        <FailureNotice
          failure={action.failure}
          fallback={action.error_message ?? action.error_kind}
          dense
          announce={false}
          tone={failureTone(action.failure?.code)}
        />
      )}
      {/* History, or the failure already leading the drawer: one line, the whole
          explanation one click away. */}
      {failed && collapsed && (
        <details className="inv-resolved">
          <summary className={history ? 'muted' : 'inv-activity-failure'}>
            {action.failure?.headline ?? 'What went wrong'}
            {action.superseded && !action.resolved ? ' · replaced by a new version' : ''}
          </summary>
          <FailureNotice
            failure={action.failure}
            fallback={action.error_message ?? action.error_kind}
            dense
            announce={false}
            tone={failureTone(action.failure?.code)}
          />
        </details>
      )}
    </li>
  );
}

/**
 * One confirmation, the same for approve, Finish in Xero, resend and raise: what will
 * happen, the confirm and cancel buttons, Escape to cancel, and focus back on the button
 * that opened it.
 */
export function InvoiceConfirm({
  label,
  children,
  confirmLabel,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  label: string;
  children: ReactNode;
  confirmLabel: string;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = panel.current?.querySelector<HTMLElement>('input, button');
    first?.focus();
    return () => opener?.focus();
  }, []);
  return (
    <div
      ref={panel}
      className="inv-confirm"
      role="dialog"
      aria-label={label}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <div className="inv-confirm-title">{label}</div>
      <div className="inv-confirm-body">{children}</div>
      <div className="inv-confirm-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary btn-sm" disabled={confirmDisabled} onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
